import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";
import { useNavColumn } from "./nav-column-state";

type ShellFrame = "document" | "canvas";

interface ShellFrameContextValue {
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
  columnOpen: boolean;
  setColumnOpen: (open: boolean) => void;
  columnWidth: number;
  setColumnWidth: (width: number) => void;
  label: string;
  setLabel: (label: string) => void;
  frame: ShellFrame;
  setFrame: (frame: ShellFrame) => void;
}

const ShellFrameContext = createContext<ShellFrameContextValue | null>(null);

const DEFAULT_LABEL = "Sidebar";

/**
 * The sidebar inset and the page's frame. The column and a page's extension
 * share one inset: one surface, one radius, one shadow, a hairline between
 * them. The page, and a trailing aside, are their own insets on the canvas.
 * A page can ask the page area to fill its height (`canvas`) instead of
 * scrolling a document.
 */
export const ShellFrameProvider = ({ children }: { children: ReactNode }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [columnOpen, setColumnOpen] = useState(false);
  const [columnWidth, setColumnWidth] = useState(280);
  const [label, setLabel] = useState(DEFAULT_LABEL);
  const [frame, setFrame] = useState<ShellFrame>("document");
  const value = useMemo(
    () => ({
      slot,
      setSlot,
      columnOpen,
      setColumnOpen,
      columnWidth,
      setColumnWidth,
      label,
      setLabel,
      frame,
      setFrame,
    }),
    [slot, columnOpen, columnWidth, label, frame],
  );
  return (
    <ShellFrameContext.Provider value={value}>
      {children}
    </ShellFrameContext.Provider>
  );
};

export const useShellColumnOpen = () =>
  useContext(ShellFrameContext)?.columnOpen ?? false;

export const useShellFrameMode = () =>
  useContext(ShellFrameContext)?.frame ?? "document";

/** The page area fills its height and the page scrolls inside itself. */
export const useShellFrame = (frame: ShellFrame) => {
  const setFrame = useContext(ShellFrameContext)?.setFrame;
  // Before paint. An effect would show a scrolling document, then snap the
  // page to a canvas and shift the layout.
  useLayoutEffect(() => {
    if (!setFrame) return;
    setFrame(frame);
    return () => setFrame("document");
  }, [frame, setFrame]);
};

/** The column and its extension, as one inset. Hidden on a phone: the drawer is the sidebar. */
export const ShellSidebar = ({ children }: { children: ReactNode }) => {
  const column = useNavColumn();
  const extended = useShellColumnOpen();
  const visible = (!column.hidden && !!column.active) || extended;
  return (
    <Stack
      direction="row"
      className={cn(
        "relative hidden min-h-0 shrink-0 self-stretch md:my-2 md:flex",
        {
          "rounded-xl bg-surface shadow-raised md:mr-2": visible,
        },
      )}
    >
      {children}
    </Stack>
  );
};

export const ShellColumnSlot = () => {
  const context = useContext(ShellFrameContext);
  const column = useNavColumn();
  const open = context?.columnOpen ?? false;
  const navOpen = !column.hidden && !!column.active;
  return (
    <aside
      ref={context?.setSlot}
      aria-label={context?.label ?? DEFAULT_LABEL}
      style={{ width: context?.columnWidth ?? 280 }}
      className={cn("hidden", {
        "relative flex h-full min-h-0 shrink-0 flex-col self-stretch overflow-hidden bg-surface":
          open,
        "rounded-e-xl border-s border-line": open && navOpen,
        "rounded-xl": open && !navOpen,
      })}
    />
  );
};

/** Portals into the shell column. On a phone, or outside the shell, it stays in the page. */
export const ShellColumn = ({
  open,
  label,
  width = 280,
  children,
}: {
  open: boolean;
  label: string;
  width?: number;
  children: ReactNode;
}) => {
  const isMobile = useIsMobile();
  const context = useContext(ShellFrameContext);
  const setColumnOpen = context?.setColumnOpen;
  const setColumnWidth = context?.setColumnWidth;
  const setLabel = context?.setLabel;

  useLayoutEffect(() => {
    if (open && setLabel) setLabel(label);
  }, [open, label, setLabel]);

  // Before paint, so the page does not draw full width and then lose the column.
  useLayoutEffect(() => {
    if (!open || !setColumnOpen || !setColumnWidth || isMobile) return;
    setColumnWidth(width);
    setColumnOpen(true);
    return () => setColumnOpen(false);
  }, [open, setColumnOpen, setColumnWidth, isMobile, width]);

  if (!open) return null;
  // No shell (a story, a test): keep the column in the page. On a phone the
  // nav column is the drawer, so the list stays in the page too. Until the
  // slot's node exists, render nothing rather than a copy in the page.
  if (!context || isMobile)
    return <Stack className="min-h-0">{children}</Stack>;
  if (!context.slot) return null;
  return createPortal(children, context.slot);
};
